import type { ApiResult, ToolId } from '@warehouselogix/shared';

import { OfflineEngine, type OfflineEnv } from './engine';
import { IdbJournal } from './queue';
import { FetchTransport } from './transport';

/**
 * The floor app's ONE network object: the offline engine wired to the real
 * browser (fetch transport, IndexedDB journal, navigator.onLine, timers,
 * crypto). Tools call floorRead/floorWrite; the StatusPill subscribes to the
 * engine's connection state; main.tsx calls settlePendingWrites() on boot so
 * anything a previous page left hanging is probed and reported.
 */

function online(): boolean {
  // navigator.onLine only ever reliably tells you NO (netShim 185).
  return !('onLine' in navigator) || navigator.onLine !== false;
}

function whenOnline(): Promise<void> {
  if (online()) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let done = false;
    const go = (): void => {
      if (done) {
        return;
      }
      done = true;
      window.removeEventListener('online', go);
      window.clearInterval(poll);
      resolve();
    };
    window.addEventListener('online', go);
    // The online event is not dependable on every scanner WebView, so poll too.
    const poll = window.setInterval(() => {
      if (online()) {
        go();
      }
    }, 1500);
  });
}

const browserEnv: OfflineEnv = {
  transport: new FetchTransport(),
  journal: new IdbJournal(),
  isOnline: online,
  whenOnline,
  delay: (ms: number) =>
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms);
    }),
  now: () => Date.now(),
  mintKey: () => crypto.randomUUID(),
};

/** Process-wide singleton — the whole floor app shares one policy + journal. */
export const floorNet = new OfflineEngine(browserEnv);

/** Read action: no side effects, retried freely, never journalled. */
export function floorRead<T>(
  tool: ToolId,
  action: string,
  payload: unknown
): Promise<ApiResult<T>> {
  return floorNet.read<T>(tool, action, payload);
}

/** Mutating action: `_ik` minted + journalled, probe-then-resend on a drop. */
export function floorWrite<T>(
  tool: ToolId,
  action: string,
  payload: unknown
): Promise<ApiResult<T>> {
  return floorNet.write<T>(tool, action, payload);
}

let settledOnce = false;

/**
 * On load: settle anything a previous page left hanging. Idempotent per page
 * load — called from the landing screens (hub and the tool routes) so it runs
 * whichever way the app is entered, but probes only once.
 */
export function settlePendingWrites(): void {
  if (settledOnce) {
    return;
  }
  settledOnce = true;
  void floorNet.settle();
}
